import React, { useRef, useState, useEffect } from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Container,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    TextField,
    Typography,
    styled,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { axiosClient } from "../AxiosClient";

const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: "center",
    color: theme.palette.text.secondary,
}));

const ButtonPrincipal = styled(Button)({
    flexGrow: 0,
    borderRadius: "20px",
    gap: "10px",
    backgroundColor: "#161e55",
    color: "#ffff",
    margin: "0px 1rem",
    "&:hover": {
        backgroundColor: "#A8C8AF",
    },
});

const PASOS = [
    { label: "Paso 2", value: "Paso 2" },
    { label: "Paso 7", value: "Paso 7" },
];

const INITIAL_EXPERIAN = {
    document: "",
    ingresosExperian: "",
    totalCuotas: "",
    totalSaldoActual: "",
    rangoEdad: "",
    fechaExpedicion: "",
    score: "",
    bucket: "",
    genero: "",
    expeditionPlace: "",
    totalCreditosAbiertosSecCoop: "",
    totalCreditosAbiertosSecFinancieros: "",
    totalCreditosAbiertosSecReal: "",
    totalCreditosAbiertosSecTelco: "",
    saldoActualCreditosSectorCoope: "",
    saldoActualCreditosSectorFinanciero: "",
    saldoActualCreditosSectorReal: "",
    saldoActualCreditosSectorTelco: "",
    totalCreditosCerradosFinYCoop: "",
    totalCreditosCerradosSecReal: "",
    totalCreditosCerradosSecTelco: "",
};

export default function DocumentoPdfExperian() {
    const fileInputRef = useRef(null);
    const [file, setFile] = useState(null);
    const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
    const [paso, setPaso] = useState(PASOS[0].value);
    const [experian, setExperian] = useState(INITIAL_EXPERIAN);
    const [validationUuid, setValidationUuid] = useState(null);
    const [loading, setLoading] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("info");
    const [sectorOccupations, setSectorOccupations] = useState([]);
    const [ingresosReportados, setIngresosReportados] = useState("");
    const [gastosReportados, setGastosReportados] = useState("");
    const [sectorOcupacionUuid, setSectorOcupacionUuid] = useState("");
    const [municipioResidencia, setMunicipioResidencia] = useState("");
    const [direccionResidencia, setDireccionResidencia] = useState("");

    useEffect(() => {
        axiosClient
            .get("/v1/sector-occupations")
            .then((response) => {
                if (response.status === 200 && Array.isArray(response.data)) {
                    setSectorOccupations(response.data);
                }
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (!file) {
            setPdfPreviewUrl(null);
            return;
        }
        const url = URL.createObjectURL(file);
        setPdfPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    const handleFileChange = (event) => {
        const selectedFile = event.target.files?.[0];
        if (selectedFile?.type === "application/pdf") {
            setFile(selectedFile);
        } else if (selectedFile) {
            setFile(null);
        }
        event.target.value = "";
    };

    const showSnackbar = (message, severity = "info") => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const getErrorMessage = (error, defaultMessage) => {
        const data = error.response?.data;
        if (data == null) return defaultMessage;
        // Respuesta en texto plano
        if (typeof data === "string" && data.trim() !== "") return data.trim();
        // Propiedades habituales del backend
        const msg =
            data.message ||
            data.mensaje ||
            data.error ||
            data.msg ||
            data.detail ||
            data.errorMessage ||
            data.title;
        if (msg != null && String(msg).trim() !== "") return String(msg).trim();
        // Lista de errores (ej. validación)
        const errors = data.errors;
        if (Array.isArray(errors) && errors.length > 0) {
            const first = errors[0];
            const firstMsg =
                typeof first === "string" ? first : first?.message || first?.msg || first?.detail;
            if (firstMsg != null && String(firstMsg).trim() !== "") return String(firstMsg).trim();
        }
        return defaultMessage;
    };

    const handleEnviar = () => {
        if (!file) return;
        setLoading(true);
        const formData = new FormData();
        formData.append("file", file);
        axiosClient
            .post("/v1/experian/structure-from-pdf", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            })
            .then((response) => {
                setLoading(false);
                const uuid = response.data?.uuid ?? response.data;
                if (uuid) {
                    setValidationUuid(typeof uuid === "string" ? uuid : String(uuid));
                    setExperian(INITIAL_EXPERIAN);
                    setIngresosReportados("");
                    setGastosReportados("");
                    setSectorOcupacionUuid("");
                    setMunicipioResidencia("");
                    setDireccionResidencia("");
                    showSnackbar("Se ha empezado a procesar el documento Experian.", "success");
                } else {
                    showSnackbar("Respuesta inesperada del servidor.", "warning");
                }
            })
            .catch((error) => {
                setLoading(false);
                showSnackbar(
                    getErrorMessage(error, "Error al enviar el PDF. Intenta de nuevo."),
                    "error"
                );
            });
    };

    const mapValidationToExperian = (data) => {
        if (!data) return INITIAL_EXPERIAN;
        const toStr = (v) => (v != null ? String(v) : "");
        return {
            document: toStr(data.documentNumber),
            ingresosExperian: toStr(data.ingresosExperian),
            totalCuotas: toStr(data.totalCuotas),
            totalSaldoActual: toStr(data.totalSaldoActual),
            rangoEdad: toStr(data.rangoEdad),
            fechaExpedicion: toStr(data.fechaExpedicion),
            score: toStr(data.score),
            bucket: toStr(data.bucket),
            genero: toStr(data.genero),
            expeditionPlace: toStr(data.lugarExpedicion),
            totalCreditosAbiertosSecCoop: toStr(data.totalCreditosAbiertosSectorCoope),
            totalCreditosAbiertosSecFinancieros: toStr(data.totalCreditosAbiertosSectorFinanciero),
            totalCreditosAbiertosSecReal: toStr(data.totalCreditosAbiertosSectorReal),
            totalCreditosAbiertosSecTelco: toStr(data.totalCreditosAbiertosSectorTelco),
            saldoActualCreditosSectorCoope: toStr(data.totalSaldoActualSectorCoope),
            saldoActualCreditosSectorFinanciero: toStr(data.totalSaldoActualSectorFinanciero),
            saldoActualCreditosSectorReal: toStr(data.totalSaldoActualSectorReal),
            saldoActualCreditosSectorTelco: toStr(data.totalSaldoActualSectorTelco),
            totalCreditosCerradosFinYCoop: toStr(data.totalCreditosCerradosSectorFinancieroCoope),
            totalCreditosCerradosSecReal: toStr(data.totalCreditosCerradosSectorReal),
            totalCreditosCerradosSecTelco: toStr(data.totalCreditosCerradosSectorTelco),
        };
    };

    const handleConsultar = () => {
        if (!validationUuid) {
            showSnackbar("Envía primero un PDF para obtener el UUID de validación.", "warning");
            return;
        }
        setLoading(true);
        axiosClient
            .get(`/v1/experian/validation/${validationUuid}`)
            .then((response) => {
                setLoading(false);
                if (response.status === 200 && response.data) {
                    const data = response.data;
                    if (data.documentNumber == null || data.documentNumber === "") {
                        showSnackbar("Se está procesando la información.", "info");
                    } else {
                        setExperian(mapValidationToExperian(data));
                        showSnackbar("Se cargó la información.", "success");
                    }
                }
            })
            .catch((error) => {
                setLoading(false);
                if (error.response?.status === 404) {
                    showSnackbar("Validación no encontrada. El procesamiento puede estar en curso.", "warning");
                } else {
                    showSnackbar(
                        getErrorMessage(error, "Error al consultar la validación."),
                        "error"
                    );
                }
            });
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === "clickaway") return;
        setSnackbarOpen(false);
    };

    const handleExperianChange = (field) => (event) => {
        setExperian((prev) => ({ ...prev, [field]: event.target.value }));
    };

    const experianToRequest = () => {
        const toNum = (v) => {
            if (v == null || v === "") return null;
            const n = Number(v);
            return isNaN(n) ? null : n;
        };
        const toInt = (v) => {
            const n = toNum(v);
            return n != null ? Math.floor(n) : null;
        };
        const body = {};
        if (experian.document != null && experian.document !== "") body.documentNumber = experian.document;
        if (experian.ingresosExperian != null && experian.ingresosExperian !== "") body.ingresosExperian = toNum(experian.ingresosExperian);
        if (experian.totalCuotas != null && experian.totalCuotas !== "") body.totalCuotas = toNum(experian.totalCuotas);
        if (experian.totalSaldoActual != null && experian.totalSaldoActual !== "") body.totalSaldoActual = toNum(experian.totalSaldoActual);
        if (experian.rangoEdad != null && experian.rangoEdad !== "") body.rangoEdad = experian.rangoEdad;
        if (experian.fechaExpedicion != null && experian.fechaExpedicion !== "") body.fechaExpedicion = experian.fechaExpedicion;
        if (experian.score != null && experian.score !== "") body.score = toInt(experian.score);
        if (experian.bucket != null && experian.bucket !== "") body.bucket = experian.bucket;
        if (experian.genero != null && experian.genero !== "") body.genero = experian.genero;
        if (experian.expeditionPlace != null && experian.expeditionPlace !== "") body.lugarExpedicion = experian.expeditionPlace;
        if (experian.totalCreditosAbiertosSecCoop != null && experian.totalCreditosAbiertosSecCoop !== "") body.totalCreditosAbiertosSectorCoope = toInt(experian.totalCreditosAbiertosSecCoop);
        if (experian.totalCreditosAbiertosSecFinancieros != null && experian.totalCreditosAbiertosSecFinancieros !== "") body.totalCreditosAbiertosSectorFinanciero = toInt(experian.totalCreditosAbiertosSecFinancieros);
        if (experian.totalCreditosAbiertosSecReal != null && experian.totalCreditosAbiertosSecReal !== "") body.totalCreditosAbiertosSectorReal = toInt(experian.totalCreditosAbiertosSecReal);
        if (experian.totalCreditosAbiertosSecTelco != null && experian.totalCreditosAbiertosSecTelco !== "") body.totalCreditosAbiertosSectorTelco = toInt(experian.totalCreditosAbiertosSecTelco);
        if (experian.saldoActualCreditosSectorCoope != null && experian.saldoActualCreditosSectorCoope !== "") body.totalSaldoActualSectorCoope = toNum(experian.saldoActualCreditosSectorCoope);
        if (experian.saldoActualCreditosSectorFinanciero != null && experian.saldoActualCreditosSectorFinanciero !== "") body.totalSaldoActualSectorFinanciero = toNum(experian.saldoActualCreditosSectorFinanciero);
        if (experian.saldoActualCreditosSectorReal != null && experian.saldoActualCreditosSectorReal !== "") body.totalSaldoActualSectorReal = toNum(experian.saldoActualCreditosSectorReal);
        if (experian.saldoActualCreditosSectorTelco != null && experian.saldoActualCreditosSectorTelco !== "") body.totalSaldoActualSectorTelco = toNum(experian.saldoActualCreditosSectorTelco);
        if (experian.totalCreditosCerradosFinYCoop != null && experian.totalCreditosCerradosFinYCoop !== "") body.totalCreditosCerradosSectorFinancieroCoope = toInt(experian.totalCreditosCerradosFinYCoop);
        if (experian.totalCreditosCerradosSecReal != null && experian.totalCreditosCerradosSecReal !== "") body.totalCreditosCerradosSectorReal = toInt(experian.totalCreditosCerradosSecReal);
        if (experian.totalCreditosCerradosSecTelco != null && experian.totalCreditosCerradosSecTelco !== "") body.totalCreditosCerradosSectorTelco = toInt(experian.totalCreditosCerradosSecTelco);
        return body;
    };

    const handleEditar = () => {
        if (!validationUuid) return;
        const hasLoadedInfo = experian.document != null && String(experian.document).trim() !== "";
        if (!hasLoadedInfo) {
            showSnackbar("Carga la información con Consultar antes de editar.", "warning");
            return;
        }
        setLoading(true);
        const body = experianToRequest();
        axiosClient
            .patch(`/v1/experian/validation/${validationUuid}`, body, {
                headers: { "Content-Type": "application/json" },
            })
            .then((response) => {
                setLoading(false);
                if (response.status === 200 && response.data) {
                    setExperian(mapValidationToExperian(response.data));
                    showSnackbar("Información actualizada correctamente.", "success");
                }
            })
            .catch((error) => {
                setLoading(false);
                if (error.response?.status === 404) {
                    showSnackbar("Validación no encontrada.", "error");
                } else {
                    showSnackbar(
                        getErrorMessage(error, "Error al actualizar la información."),
                        "error"
                    );
                }
            });
    };

    const hasLoadedInfo = experian.document != null && String(experian.document).trim() !== "";

    const handleGenerarEstudioViabilidad = () => {
        const pasoNum = paso === "Paso 7" ? 7 : 2;
        if (pasoNum !== 2 && pasoNum !== 7) {
            showSnackbar("El paso debe ser 2 o 7.", "warning");
            return;
        }
        if (!validationUuid) {
            showSnackbar("No hay validación Experian. Envía un PDF primero.", "warning");
            return;
        }

        let ingresosNum = 0;
        let gastosNum = 0;
        let sectorOcupacionId =
            pasoNum === 7 && sectorOccupations.length > 0 ? sectorOccupations[0].uuid : null;
        let municipio = "";
        let direccion = "";

        if (pasoNum === 2) {
            const ingresosOk = ingresosReportados != null && String(ingresosReportados).trim() !== "";
            const gastosOk = gastosReportados != null && String(gastosReportados).trim() !== "";
            const sectorOk = sectorOcupacionUuid != null && String(sectorOcupacionUuid).trim() !== "";
            const municipioOk = municipioResidencia != null && String(municipioResidencia).trim() !== "";
            const direccionOk = direccionResidencia != null && String(direccionResidencia).trim() !== "";
            if (!ingresosOk || !gastosOk || !sectorOk || !municipioOk || !direccionOk) {
                showSnackbar("Completa todos los campos de Información adicional cliente.", "warning");
                return;
            }
            ingresosNum = Number(String(ingresosReportados).trim().replace(",", "."));
            gastosNum = Number(String(gastosReportados).trim().replace(",", "."));
            if (isNaN(ingresosNum) || isNaN(gastosNum)) {
                showSnackbar("Ingresos y gastos deben ser valores numéricos.", "warning");
                return;
            }
            sectorOcupacionId = String(sectorOcupacionUuid).trim();
            municipio = String(municipioResidencia).trim();
            direccion = String(direccionResidencia).trim();
        }

        setLoading(true);
        const body = {
            experianValidationUuid: validationUuid,
            ingresos: ingresosNum,
            gastos: gastosNum,
            sectorOcupacionId,
            municipio,
            direccion,
            paso: pasoNum,
        };
        axiosClient
            .post("/v1/experian/process-after-pdf", body, {
                headers: { "Content-Type": "application/json" },
            })
            .then((response) => {
                setLoading(false);
                if (response.status === 200) {
                    showSnackbar("Estudio de viabilidad generado correctamente.", "success");
                }
            })
            .catch((error) => {
                setLoading(false);
                if (error.response?.status === 400) {
                    showSnackbar(
                        getErrorMessage(
                            error,
                            "Solicitud inválida. Revisa que el paso sea 2 o 7, que la validación tenga documento y que el cliente exista con admin asociado."
                        ),
                        "error"
                    );
                } else {
                    showSnackbar(
                        getErrorMessage(error, "Error al generar el estudio de viabilidad."),
                        "error"
                    );
                }
            });
    };

    return (
        <React.Fragment>
            <br />
            <Box sx={{ width: "100%", marginTop: "1.7rem" }}>
                <Container>
                    <Item></Item>
                    <Item>.</Item>
                    <Grid container spacing={4} sx={{ justifyContent: "space-between", alignItems: "center" }}>
                        <Grid item>
                            <h1>Documento Pdf Experian</h1>
                        </Grid>
                    </Grid>
                    <Grid container spacing={2} sx={{ mt: 2 }}>
                        <Grid item xs={12}>
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 2.5,
                                    display: "flex",
                                    flexWrap: "wrap",
                                    alignItems: "flex-end",
                                    gap: 3,
                                }}
                            >
                                <FormControl sx={{ minWidth: 180 }} size="small">
                                    <InputLabel id="paso-select-label">Paso</InputLabel>
                                    <Select
                                        labelId="paso-select-label"
                                        id="paso-select"
                                        value={paso}
                                        label="Paso"
                                        onChange={(e) => setPaso(e.target.value)}
                                    >
                                        {PASOS.map((p) => (
                                            <MenuItem key={p.value} value={p.value}>
                                                {p.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 0.5,
                                        flex: "1 1 280px",
                                        minWidth: 0,
                                    }}
                                >
                                    <Typography
                                        component="label"
                                        variant="body2"
                                        sx={{
                                            fontWeight: 500,
                                            color: "text.secondary",
                                        }}
                                    >
                                        Archivo PDF
                                    </Typography>
                                    {file && (
                                        <Typography variant="body2" color="text.secondary" noWrap>
                                            {file.name}
                                        </Typography>
                                    )}
                                    <Box sx={{ display: "flex", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
                                        <input
                                            accept=".pdf,application/pdf"
                                            type="file"
                                            hidden
                                            ref={fileInputRef}
                                            onChange={handleFileChange}
                                        />
                                        <ButtonPrincipal
                                            type="button"
                                            size="small"
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            Seleccionar archivo
                                        </ButtonPrincipal>
                                        <ButtonPrincipal
                                            type="button"
                                            size="small"
                                            disabled={!file}
                                            onClick={handleEnviar}
                                            sx={{
                                                "&.Mui-disabled": {
                                                    color: "rgba(255,255,255,0.7)",
                                                    backgroundColor: "rgba(22, 30, 85, 0.5)",
                                                },
                                            }}
                                        >
                                            Enviar
                                        </ButtonPrincipal>
                                    </Box>
                                </Box>
                            </Paper>
                        </Grid>
                        <Grid item xs={12}>
                            <Accordion defaultExpanded={false}>
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    aria-controls="visor-pdf-content"
                                    id="visor-pdf-header"
                                >
                                    <Typography component="span" sx={{ fontWeight: 600 }}>
                                        {file ? `Visor PDF — ${file.name}` : "Visor PDF"}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ p: 0, overflow: "hidden" }}>
                                    {pdfPreviewUrl ? (
                                        <iframe
                                            src={pdfPreviewUrl}
                                            title="Visor PDF Experian"
                                            width="100%"
                                            height="600"
                                            style={{ border: "none" }}
                                        />
                                    ) : (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                minHeight: 320,
                                                color: "text.secondary",
                                                py: 3,
                                            }}
                                        >
                                            <Typography>
                                                Carga un PDF para visualizarlo
                                            </Typography>
                                        </Box>
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        </Grid>
                        {validationUuid && (
                        <Grid item xs={12}>
                            <Box sx={{ mb: 2 }}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5,
                                        mb: 2,
                                    }}
                                >
                                    <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                                        Información
                                    </Typography>
                                    <ButtonPrincipal type="button" size="small" onClick={handleConsultar}>
                                        Consultar
                                    </ButtonPrincipal>
                                    <ButtonPrincipal
                                        type="button"
                                        size="small"
                                        disabled={!hasLoadedInfo}
                                        onClick={handleEditar}
                                        sx={{
                                            "&.Mui-disabled": {
                                                color: "rgba(255,255,255,0.7)",
                                                backgroundColor: "rgba(22, 30, 85, 0.5)",
                                            },
                                        }}
                                    >
                                        Editar
                                    </ButtonPrincipal>
                                    {paso === "Paso 7" && (
                                        <ButtonPrincipal
                                            type="button"
                                            size="small"
                                            onClick={handleGenerarEstudioViabilidad}
                                        >
                                            Generar estudio viabilidad
                                        </ButtonPrincipal>
                                    )}
                                </Box>
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Número documento"
                                                value={experian.document ?? ""}
                                                onChange={handleExperianChange("document")}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Ingresos Experian"
                                                value={experian.ingresosExperian ?? ""}
                                                onChange={handleExperianChange("ingresosExperian")}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Total cuotas"
                                                value={experian.totalCuotas ?? ""}
                                                onChange={handleExperianChange("totalCuotas")}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Total saldo actual"
                                                value={experian.totalSaldoActual ?? ""}
                                                onChange={handleExperianChange("totalSaldoActual")}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Rango edad"
                                                value={experian.rangoEdad ?? ""}
                                                onChange={handleExperianChange("rangoEdad")}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Fecha expedición"
                                                value={experian.fechaExpedicion ?? ""}
                                                onChange={handleExperianChange("fechaExpedicion")}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Score"
                                                value={experian.score ?? ""}
                                                onChange={handleExperianChange("score")}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Bucket"
                                                value={experian.bucket ?? ""}
                                                onChange={handleExperianChange("bucket")}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Género"
                                                value={experian.genero ?? ""}
                                                onChange={handleExperianChange("genero")}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Lugar expedición documento"
                                                value={experian.expeditionPlace ?? ""}
                                                onChange={handleExperianChange("expeditionPlace")}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1, mb: 0.5 }}>
                                                Créditos abiertos
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Sector Cooperativo"
                                                value={experian.totalCreditosAbiertosSecCoop ?? ""}
                                                onChange={handleExperianChange("totalCreditosAbiertosSecCoop")}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Sector Financiero"
                                                value={experian.totalCreditosAbiertosSecFinancieros ?? ""}
                                                onChange={handleExperianChange("totalCreditosAbiertosSecFinancieros")}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Sector Real"
                                                value={experian.totalCreditosAbiertosSecReal ?? ""}
                                                onChange={handleExperianChange("totalCreditosAbiertosSecReal")}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Sector Telco"
                                                value={experian.totalCreditosAbiertosSecTelco ?? ""}
                                                onChange={handleExperianChange("totalCreditosAbiertosSecTelco")}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1, mb: 0.5 }}>
                                                Saldo actual
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Sector Cooperativo"
                                                value={experian.saldoActualCreditosSectorCoope ?? ""}
                                                onChange={handleExperianChange("saldoActualCreditosSectorCoope")}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Sector Financiero"
                                                value={experian.saldoActualCreditosSectorFinanciero ?? ""}
                                                onChange={handleExperianChange("saldoActualCreditosSectorFinanciero")}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Sector Real"
                                                value={experian.saldoActualCreditosSectorReal ?? ""}
                                                onChange={handleExperianChange("saldoActualCreditosSectorReal")}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Sector Telco"
                                                value={experian.saldoActualCreditosSectorTelco ?? ""}
                                                onChange={handleExperianChange("saldoActualCreditosSectorTelco")}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1, mb: 0.5 }}>
                                                Créditos cerrados
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Sector Financiero y Cooperativo"
                                                value={experian.totalCreditosCerradosFinYCoop ?? ""}
                                                onChange={handleExperianChange("totalCreditosCerradosFinYCoop")}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Sector Real"
                                                value={experian.totalCreditosCerradosSecReal ?? ""}
                                                onChange={handleExperianChange("totalCreditosCerradosSecReal")}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Sector Telco"
                                                value={experian.totalCreditosCerradosSecTelco ?? ""}
                                                onChange={handleExperianChange("totalCreditosCerradosSecTelco")}
                                            />
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Box>
                        </Grid>
                        )}
                        {hasLoadedInfo && paso === "Paso 2" && (
                        <Grid item xs={12}>
                            <Box sx={{ mb: 2 }}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5,
                                        mb: 2,
                                    }}
                                >
                                    <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                                        Información adicional cliente
                                    </Typography>
                                    <ButtonPrincipal
                                        type="button"
                                        size="small"
                                        onClick={handleGenerarEstudioViabilidad}
                                    >
                                        Generar estudio viabilidad
                                    </ButtonPrincipal>
                                </Box>
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Ingresos reportados"
                                                value={ingresosReportados}
                                                onChange={(e) => setIngresosReportados(e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Gastos reportados"
                                                value={gastosReportados}
                                                onChange={(e) => setGastosReportados(e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel id="sector-ocupacion-label">Sector ocupación</InputLabel>
                                                <Select
                                                    labelId="sector-ocupacion-label"
                                                    id="sector-ocupacion-select"
                                                    value={sectorOcupacionUuid}
                                                    label="Sector ocupación"
                                                    onChange={(e) => setSectorOcupacionUuid(e.target.value)}
                                                >
                                                    {sectorOccupations.map((sector) => (
                                                        <MenuItem key={sector.uuid} value={sector.uuid}>
                                                            {sector.name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Municipio de residencia"
                                                value={municipioResidencia}
                                                onChange={(e) => setMunicipioResidencia(e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Dirección de residencia"
                                                value={direccionResidencia}
                                                onChange={(e) => setDireccionResidencia(e.target.value)}
                                            />
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Box>
                        </Grid>
                        )}
                    </Grid>
                </Container>
            </Box>
            <Backdrop
                sx={{
                    background: "rgba(255,255,255,0.1)",
                    zIndex: 5,
                    width: "50%",
                    margin: "auto auto auto 30vw",
                }}
                open={loading}
            >
                <CircularProgress color="success" />
            </Backdrop>
            <Snackbar open={snackbarOpen} autoHideDuration={5000} onClose={handleCloseSnackbar}>
                <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: "100%" }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </React.Fragment>
    );
}
